import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import
{
  clampKeysetPageSize,
  KEYSET_MAX_PAGE_SIZE,
  KeysetNavigationDirection,
  KeysetPagingMeta,
  KeysetPagingResultMeta
} from 'src/app/core/keyset-paging/keyset-paging-meta';
import { KeysetSortStrategy } from 'src/app/core/keyset-paging/keyset-paging-sort';
import { KeysetPagingSession } from 'src/app/core/keyset-paging/keyset-paging-session';
import
{
  applyKeysetFirstPageIntent,
  buildKeysetSearchBodyFromGridState,
  resolveKeysetFirstPageIntent,
  runKeysetGridSearch
} from 'src/app/core/keyset-paging/keyset-paging-search';

/** Configuration for {@link KeysetPagingStore} (one store per grid). */
export interface KeysetPagingStoreOptions<TContext = any>
{
  sortStrategy: KeysetSortStrategy<TContext>;
  getSortContext: () => TContext;
  getBodyExtra: () => Record<string, any>;
  getAnchorExtras: () => Record<string, any>;
  getState: () => State;
  postKeyset: (body: Record<string, any>, meta: KeysetPagingMeta) => void;
  isEnabled: () => boolean;
  /** Per-grid server cap (default {@link KEYSET_MAX_PAGE_SIZE}). Cases use {@link CASE_KEYSET_MAX_PAGE_SIZE}. */
  maxPageSize?: number;
}

/** Grid row emitted by {@link GridDataService.searchGridKeyset} (includes optional keyset metadata). */
export type KeysetGridServiceRow = GridDataResult & { keysetPaging?: KeysetPagingResultMeta };

/** Values to assign to list grid fields after {@link KeysetPagingStore.bindServiceGridRow}. */
export interface KeysetListGridBindResult
{
  data: any[];
  total: number;
  gridViewTotal: number;
}

/** Same as {@link KeysetPagingStoreOptions} when body/anchor/sort context share one keyed flag (case/member lists). */
export interface KeysetPagingKeyedSortContextOptions<TContext extends { recentOnly: boolean }>
{
  sortStrategy: KeysetSortStrategy<TContext>;
  getState: () => State;
  postKeyset: (body: Record<string, any>, meta: KeysetPagingMeta) => void;
  isEnabled: () => boolean;
  isRecentViewContext: () => boolean;
  maxPageSize?: number;
}

/**
 * Single place for keyset paging: session, grid dataState branch, first-page / pager actions,
 * and POST wiring. Instantiate once per list component and pass to {@link KeysetPagingComponent}.
 */
export class KeysetPagingStore<TContext = any>
{
  public readonly session = new KeysetPagingSession();

  private lastSortFingerprint: string | null = null;

  constructor(private readonly options: KeysetPagingStoreOptions<TContext>) { }

  private get maxPageSize(): number
  {
    return this.options.maxPageSize ?? KEYSET_MAX_PAGE_SIZE;
  }

  /** Factory when anchor/body/sort context are keyed off {@link KeysetPagingKeyedSortContextOptions.isRecentViewContext}. */
  public static forKeyedSortContext<TContext extends { recentOnly: boolean }>(
    options: KeysetPagingKeyedSortContextOptions<TContext>
  ): KeysetPagingStore<TContext>
  {
    return new KeysetPagingStore({
      sortStrategy: options.sortStrategy,
      getSortContext: (): TContext => ({ recentOnly: options.isRecentViewContext() } as TContext),
      getBodyExtra: () => ({ recentOnly: options.isRecentViewContext() }),
      getAnchorExtras: () => ({ recent: options.isRecentViewContext() }),
      getState: options.getState,
      postKeyset: options.postKeyset,
      isEnabled: options.isEnabled,
      maxPageSize: options.maxPageSize
    });
  }

  /**
   * Kendo grid dataStateChange when using keyset paging. Returns true if handled (skip offset query).
   */
  public handleGridDataStateChange(state: State): boolean
  {
    if (!this.options.isEnabled())
    {
      return false;
    }
    const sortFingerprint = JSON.stringify(state.sort ?? []);
    if (this.lastSortFingerprint === null)
    {
      this.syncSortFingerprint(state);
    }
    else if (sortFingerprint !== this.lastSortFingerprint)
    {
      this.syncSortFingerprint(state);
      this.resetToFirstPage(state);
      return true;
    }

    const anchorExtras = this.options.getAnchorExtras();
    const anchor = this.session.computeAnchorFingerprint(state, anchorExtras);
    if (anchor !== this.session.committedAnchor)
    {
      this.session.resetNavigationState();
      this.syncSortFingerprint(state);
      this.runFirstPageIntent(state, true);
      return true;
    }
    if (this.session.hasPageSizeChanged(state.take))
    {
      this.resetToFirstPage(state);
      return true;
    }
    this.options.getState().skip = this.session.virtualSkipValue;
    return true;
  }

  /** Initial / refresh query path when the list uses keyset paging. */
  public runListQueryKeysetFirstPage(state: State): void
  {
    if (!this.options.isEnabled())
    {
      return;
    }
    this.session.resetNavigationState();
    this.syncSortFingerprint(state);
    this.runFirstPageIntent(state, true);
  }

  /** After assigning grid state from Kendo: keyset path, or run standard offset query. */
  public onDataStateChangeElse(state: State, elseRunOffsetQuery: () => void): void
  {
    if (!this.handleGridDataStateChange(state))
    {
      elseRunOffsetQuery();
    }
  }

  /** Initial list query: keyset first page when enabled, otherwise caller’s standard query. */
  public runInitialQueryElse(state: State, elseQueryByState: (state: State) => void): void
  {
    if (this.options.isEnabled())
    {
      this.runListQueryKeysetFirstPage(state);
      return;
    }
    elseQueryByState(state);
  }

  /**
   * Maps a list-service {@link GridDataResult} to grid bind values; updates {@link KeysetPagingStoreOptions.getState} skip when in keyset mode.
   * Returns null when {@code value} is null. Call after row decoration if the list mutates {@code data} in place.
   */
  public bindServiceGridRow(value: GridDataResult | null): KeysetListGridBindResult | null
  {
    if (value === null)
    {
      return null;
    }
    const data = value.data ?? [];
    const total = value.total ?? 0;
    const binding = this.bindListGridResponse(
      (value as KeysetGridServiceRow).keysetPaging,
      total,
      data.length
    );
    if (binding.mode === 'keyset')
    {
      this.options.getState().skip = binding.virtualSkip;
    }
    return { data, total, gridViewTotal: binding.gridViewTotal };
  }

  /**
   * Kendo {@code pageChange} when page size changes — refetch from the first page at the new size.
   */
  public onPageSizeChange(): void
  {
    if (!this.options.isEnabled())
    {
      return;
    }
    this.resetToFirstPage(this.options.getState());
  }

  public pagerFirst(): void
  {
    if (!this.options.isEnabled())
    {
      return;
    }
    const state = this.options.getState();
    applyKeysetFirstPageIntent(
      resolveKeysetFirstPageIntent(this.session, state, this.options.getAnchorExtras(), true, this.maxPageSize),
      (navigationDirection, cursor, size, meta) =>
        this.runSearch(navigationDirection, cursor, size, meta),
      (virtualSkip) => { this.options.getState().skip = virtualSkip; }
    );
  }

  public pagerNext(): void
  {
    if (!this.options.isEnabled() || !this.session.hasMore || this.session.nextCursor == null)
    {
      return;
    }
    const take = this.options.getState().take;
    this.runSearch('NEXT', this.session.nextCursor, take, {
      requestNavigationDirection: 'NEXT',
      requestSize: take,
      hadRequestCursor: true,
      requestMode: 'NAV_NEXT'
    });
  }

  public pagerPrev(): void
  {
    if (!this.options.isEnabled() || !this.session.hasPrevious || this.session.prevCursor == null)
    {
      return;
    }
    const take = this.options.getState().take;
    this.runSearch('PREV', this.session.prevCursor, take, {
      requestNavigationDirection: 'PREV',
      requestSize: take,
      hadRequestCursor: true,
      requestMode: 'NAV_PREV'
    });
  }

  public pagerLast(): void
  {
    if (!this.options.isEnabled() || !this.session.hasMore)
    {
      return;
    }
    const take = this.options.getState().take;
    this.runSearch('LAST', null, take, {
      requestNavigationDirection: 'LAST',
      requestSize: take,
      hadRequestCursor: false,
      requestMode: 'NAV_LAST'
    });
  }

  private resolvePageSize(size: number): number
  {
    return clampKeysetPageSize(size, this.maxPageSize);
  }

  private applyKeysetResponse(
    keysetPaging: KeysetPagingResultMeta,
    rowTotal: number,
    dataLength: number
  ): { displayTotal: number; virtualSkip: number }
  {
    this.session.applyServerResult(keysetPaging, rowTotal, dataLength);
    return {
      displayTotal: this.session.displayTotal(dataLength),
      virtualSkip: this.session.virtualSkipValue
    };
  }

  private bindListGridResponse(
    keysetPaging: KeysetPagingResultMeta | undefined,
    rowTotal: number,
    dataLength: number
  ): { mode: 'keyset'; gridViewTotal: number; virtualSkip: number } | { mode: 'offset'; gridViewTotal: number }
  {
    if (this.options.isEnabled() && keysetPaging)
    {
      const { displayTotal, virtualSkip } = this.applyKeysetResponse(keysetPaging, rowTotal, dataLength);
      return { mode: 'keyset', gridViewTotal: displayTotal, virtualSkip };
    }
    return { mode: 'offset', gridViewTotal: rowTotal };
  }

  private resetToFirstPage(state: State): void
  {
    this.session.resetNavigationState();
    this.syncSortFingerprint(state);
    state.skip = 0;
    this.runFirstPageIntent(state, true);
  }

  private syncSortFingerprint(state: State): void
  {
    this.lastSortFingerprint = JSON.stringify(state.sort ?? []);
  }

  private runFirstPageIntent(state: State, force: boolean): void
  {
    applyKeysetFirstPageIntent(
      resolveKeysetFirstPageIntent(this.session, state, this.options.getAnchorExtras(), force, this.maxPageSize),
      (navigationDirection, cursor, size, meta) =>
        this.runSearch(navigationDirection, cursor, size, meta),
      (virtualSkip) => { this.options.getState().skip = virtualSkip; }
    );
  }

  private runSearch(
    navigationDirection: KeysetNavigationDirection,
    cursor: any | null,
    size: number,
    meta: KeysetPagingMeta
  ): void
  {
    const resolvedSize = this.resolvePageSize(size);
    const state = this.options.getState();
    if (state.take !== resolvedSize)
    {
      state.take = resolvedSize;
    }
    const resolvedMeta: KeysetPagingMeta = meta.requestSize === resolvedSize
      ? meta
      : { ...meta, requestSize: resolvedSize };
    runKeysetGridSearch(
      (navigationDirection, cursor) => buildKeysetSearchBodyFromGridState(
        this.options.getState(),
        this.options.sortStrategy,
        this.options.getSortContext(),
        navigationDirection,
        cursor,
        resolvedSize,
        this.options.getBodyExtra(),
        this.maxPageSize
      ),
      this.options.postKeyset,
      navigationDirection,
      cursor,
      resolvedSize,
      resolvedMeta
    );
  }
}
